/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */
import { IPersonaProps, PersonaInitialsColor } from '@fluentui/react/lib/Persona';
import { LogCategory } from '../logger';

import { ApprovalPersonaProps } from '../models/ApprovalPersonaProps';
import { getThemedIcon, IconName } from '../resources/icons/Icons';
import { ExpandValue, TeamTagClient, TeamTagResult } from '../service/gen';
import { PeopleResult } from '../service/gen/templates';
import {
    filterOutAlreadySelectedPeople,
    getPeopleResultsForQuery,
    getPeopleResultsForQueryInTeam,
    getTenantUsers,
    mapPeopleResultsToPersonas,
    normalizeQueryInput,
    sortByText,
} from './PeoplePickerUtils';
import { getAppHost } from './TeamsUtils';
import * as Utils from './Utils';

const teamtagClient = new TeamTagClient();

/**
 * Get Personas from server side. No filtering or sorting.
 *
 * @param currentPersonas - current personas selected in a list of personas
 * @param people - list of personas
 * @param filterText - text used to filter results
 * @param teamId - team id of a Teams team
 * @return People Results.
 */
export const getPeopleAndTagResultsForQueryInPersonalApp = async (
    people: PeopleResult[],
    filterText: string,
    currentPersonas: IPersonaProps[],
    teamId?: string
): Promise<IPersonaProps[]> => {
    const logger = getAppHost().getLogger();
    let peopleList = people || [];

    // generaly, filter text should not be null or undefined, but if it is, let's make it the empty string
    if (filterText == null || filterText === undefined) {
        throw new Error('Unexpected value.');
    }

    // Lowercase the user input and strip leading and following spaces
    filterText = filterText.toLowerCase().trim();
    if (teamId) {
        try {
            const [peopleResults, teamTagResults] = await Promise.all([
                getTenantUsers(filterText),
                getFilteredTeamTags(filterText, teamId),
            ]);
            const peoplePersonnas = sortByText(mapPeopleResultsToPersonas(peopleList.concat(peopleResults)));
            const teamTagPersonnas = sortByText(mapTeamTagResultsToPersonas(teamTagResults));
            let suggestedPersonas = peoplePersonnas.concat(teamTagPersonnas);
            suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
            return suggestedPersonas;
        } catch (error) {
            logger.logTraceError(
                LogCategory.PeoplePicker,
                `Failed to getPeopleAndTagResultsForQueryInPersonalApp: ${error}`
            );
            return [];
        }
    }

    // Fetch results from server in global context
    try {
        let searchList = await getTenantUsers(filterText);
        searchList = Utils.removeDuplicates(
            searchList,
            people,
            (a: PeopleResult, b: PeopleResult): boolean => a.UserAadObjectId === b.UserAadObjectId
        );
        peopleList = peopleList.concat(searchList);
    } catch (ex) {
        // do nothing, this lets us test by catching a TokenNotInitialized error
    }

    let suggestedPersonas = mapPeopleResultsToPersonas(peopleList);
    suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
    return sortByText(suggestedPersonas);
};

/**
 * Get Personas in specific team.
 *
 * @param filterText - text used to filter results
 * @param teamId - team id of a Teams team
 * @param currentPersonas - current personas selected in a list of personas
 * @return ApprovalPersonaProps list
 */
export const getPeopleResultsandTeamTagResultsForQueryInTeam = async (
    filterText: string,
    teamId: string,
    currentPersonas: ApprovalPersonaProps[]
): Promise<ApprovalPersonaProps[]> => {
    const logger = getAppHost().getLogger();
    try {
        const [peoplePersonnas, teamTagResults] = await Promise.all([
            getPeopleResultsForQueryInTeam(filterText, teamId, []),
            getFilteredTeamTags(filterText, teamId),
        ]);
        const teamTagPersonnas = sortByText(mapTeamTagResultsToPersonas(teamTagResults));
        let suggestedPersonas = peoplePersonnas.concat(teamTagPersonnas);
        suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
        return suggestedPersonas;
    } catch (error) {
        logger.logTraceError(
            LogCategory.PeoplePicker,
            `Failed to getPeopleResultsandTeamTagResultsForQueryInTeam: ${error}`
        );
        return [];
    }
};

/**
 * Convert list of TeamTags to list of IPersonaProps
 *
 * @param teamTagList - list of TeamTagResult
 * @return PersonaPropsList
 **/
const mapTeamTagResultsToPersonas = (teamTagList: TeamTagResult[]): IPersonaProps[] => {
    const loc = getAppHost().getLocale();
    const teamTagIcon = getThemedIcon(IconName.teamTagIcon);
    return teamTagList.map((tag: TeamTagResult): IPersonaProps => {
        const result: ApprovalPersonaProps = {
            text: tag.DisplayName,
            secondaryText: Utils.stringFormat(loc.strings.teamtag_members_description, {
                totalNumber: `${tag.MemberCount}`,
            }),
            id: tag.Id,
            onRenderInitials: (): JSX.Element => teamTagIcon,
            initialsColor: PersonaInitialsColor.transparent,
            coinSize: getAppHost().isMobile() ? 36 : 30,
            isTeamTag: true,
            teamTagMemberCount: tag.MemberCount,
            teamId: tag.TeamId,
        };
        return result;
    });
};

/**
 * Get the filtered tags for the team context.
 *
 * @param queryString Input string used to filter users
 * @param teamId Team ID for the group context.
 * @return People Results.
 */
export const getFilteredTeamTags = async (queryString: string, teamId: string): Promise<TeamTagResult[]> => {
    const normalizedInput = normalizeQueryInput(queryString);
    /* If we should provide teamtag */
    const teamTagEnabled =
        getAppHost().getEcsConfig().MicrosoftTeamApprovals.OnlineWorkflowSchemaVersion !== 'v1.0' &&
        getAppHost().getEcsConfig().MicrosoftTeamApprovals.TeamTagEnabled;
    if (normalizedInput && teamId && teamTagEnabled) {
        const teamTagResults = await teamtagClient.searchTeamTagResults(teamId, {
            QueryText: normalizedInput,
        });

        return teamTagResults;
    }

    return Promise.resolve([]);
};

/**
 * Get the filtered tags for the team context.
 *
 * @param teamId Team ID for the group context.
 * @param tagId Tag ID for a Teams Tag.
 * @param expand the value will descide if the team tag results contain tag members or tag members with photo.
 * @return TeamTag Result.
 */
export const getTeamTag = async (
    teamId?: string,
    tagId?: string,
    expand?: ExpandValue,
    currentChannelId?: string
): Promise<TeamTagResult> => {
    if (!teamId || !tagId) {
        return;
    }
    return await teamtagClient.getTeamTagDetail(teamId, tagId, expand, currentChannelId);
};

/**
 * Get the filtered tags for the team context.
 *
 * @param teamId Team ID for the group context.
 * @param tagId Tag ID for a Teams Tag.
 * @param expand the value will descide if the team tag results contain tag members or tag members with photo.
 * @return TeamTag Results.
 */
export const batchGetTeamTag = async (
    teamId: string,
    tagIds: string[],
    expand?: ExpandValue
): Promise<TeamTagResult[]> => {
    if (!teamId || !tagIds?.length) {
        return;
    }
    // Batch reads hydrate existing approval details, which do not carry channel context.
    const requests = tagIds.map(
        (tId: string): Promise<TeamTagResult> => teamtagClient.getTeamTagDetail(teamId, tId, expand, undefined)
    );
    return Promise.all(requests);
};

/**
 * Get Personas from Local 'conversation' roster cache
 *
 * @param currentPersonas - current personas selected in a list of personas
 * @param people - list of personas
 * @param filterText - text used to filter results
 * @param teamId - team id of a Teams team
 * @return Persona List
 */
export const getPeopleResultsAndTagResultsForQuery = async (
    people: PeopleResult[],
    filterText: string,
    currentPersonas: IPersonaProps[],
    teamId?: string
): Promise<IPersonaProps[]> => {
    try {
        const [peoplePersonnas, teamTagResults] = await Promise.all([
            getPeopleResultsForQuery(people, filterText, [], teamId),
            getFilteredTeamTags(filterText, teamId),
        ]);
        const teamTagPersonnas = sortByText(mapTeamTagResultsToPersonas(teamTagResults));
        let suggestedPersonas = peoplePersonnas.concat(teamTagPersonnas);
        suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
        return suggestedPersonas;
    } catch (ex) {
        return currentPersonas;
    }
};
